import type { ColDef, ICellRendererParams, PostSortRowsParams, RowClassParams } from 'ag-grid-community';
import { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ownerGroupLabel } from '../ownerGroup';
import { useCurrentUser } from '../auth/CurrentUserProvider';
import { codeTypeLabel, codeTypeOf } from '../codeType';
import { Link } from 'react-router-dom';
import type { QrCode } from '../api/types';
import {
  COMPACT_SCREEN,
  LARGE_SCREEN,
  MEDIUM_SCREEN,
  PHONE_SCREEN,
  useMediaQuery,
} from '../hooks/useMediaQuery';
import { CopyButton } from './CopyButton';
import { DataGrid } from './DataGrid';

/* Hoisted: handed fresh functions, the grid re-reads its columns and its sort on every keystroke. */
const rowId = (qrCode: QrCode) => qrCode.id;

const rowClass = ({ data }: RowClassParams<QrCode>) => (data?.isArchived ? 'row-archived' : undefined);

/* Keeps retired codes last after the reader sorts a column too, as the API already returns them. */
const sortArchivedLast = ({ nodes }: PostSortRowsParams<QrCode>) => {
  const live = nodes.filter((node) => !node.data?.isArchived);
  const archived = nodes.filter((node) => node.data?.isArchived);

  nodes.length = 0;
  nodes.push(...live, ...archived);
};

export interface QrCodeGridProps {
  qrCodes: QrCode[];
  loading?: boolean;
  onArchive?: (qrCode: QrCode) => void;
  onRestore?: (qrCode: QrCode) => void;
  onDelete?: (qrCode: QrCode) => void;
  onSelectionChange?: (qrCodes: QrCode[]) => void;
  emptyMessage: string;
  /** The SDS product column, only worth its width when the list is filtered to SDS product codes. */
  showProduct?: boolean;
}

export function QrCodeGrid({
  qrCodes,
  loading = false,
  onArchive,
  onRestore,
  onDelete,
  onSelectionChange,
  emptyMessage,
  showProduct = false,
}: QrCodeGridProps) {
  const { t } = useTranslation();
  const personalSpaceId = useCurrentUser().user?.personalSpaceId;

  // The buttons' handlers, behind an identity that never changes: the page rebuilds them whenever a filter
  // changes, and a column definition that changes makes the grid rebuild every cell.
  const handlers = useRef({ onArchive, onRestore, onDelete });
  handlers.current = { onArchive, onRestore, onDelete };

  /*
   * How much of the table there is room for, against the width a card has inside the screen. The buttons are
   * never dropped: everything dropped here is on the code's own page one tap away.
   *
   * The budgets: buttons 132-150, name 120 at the very least, printed link 200, destination 160, scans 90,
   * group 100, state 130. All seven need 970px, which is why the state waits for 1200.
   */
  const phone = useMediaQuery(PHONE_SCREEN);
  const compact = useMediaQuery(COMPACT_SCREEN);
  const medium = useMediaQuery(MEDIUM_SCREEN);
  const large = useMediaQuery(LARGE_SCREEN);

  const keptByWidth = phone
    ? ['title', 'actions']
    : compact
      ? ['title', 'redirectUrl', 'actions']
      : medium
        ? ['title', 'redirectUrl', 'targetUrl', 'actions']
        : large
          ? ['title', 'kind', 'redirectUrl', 'targetUrl', 'totalScans', 'ownerGroupName', 'actions']
          : null;

  // An SDS product code's destination is derived from its product, so the product takes the destination's place.
  const kept = showProduct
    ? (keptByWidth?.map((field) => (field === 'targetUrl' ? 'productName' : field)) ?? null)
    : keptByWidth;

  // Without a column of its own the state travels with the name as a dot, named in words for a screen reader.
  const stateWithName = kept !== null;
  const narrow = compact;

  const stateOf = (qrCode: QrCode) =>
    qrCode.isArchived ? 'archived' : qrCode.isActive ? 'active' : 'inactive';

  const label = (qrCode: QrCode) =>
    ({
      archived: t('qrCode.archived'),
      active: t('qrCode.active'),
      inactive: t('qrCode.inactive'),
    })[stateOf(qrCode)];

  const columns = useMemo<ColDef<QrCode>[]>(
    () => [
      {
        field: 'title',
        headerName: t('qrCode.title'),
        flex: 2,
        minWidth: phone ? 120 : 140,
        cellRenderer: ({ data }: ICellRendererParams<QrCode>) =>
          data && (
            <span className="d-flex align-items-center gap-2 w-100">
              {stateWithName && (
                <i
                  className={`pi pi-circle-fill status-dot flex-shrink-0 ${stateOf(data)}`}
                  role="img"
                  aria-label={label(data)}
                  title={label(data)}
                />
              )}
              <Link to={`/qr/${data.id}`} className="text-truncate cell-truncate" title={data.title}>
                {data.title}
              </Link>
              {!data.canEdit && (
                <span className="badge badge-neutral flex-shrink-0">
                  <i className="pi pi-lock me-1" aria-hidden="true" />
                  {t('qrCode.readOnly')}
                </span>
              )}
            </span>
          ),
      },
      {
        field: 'kind',
        headerName: t('qrCode.kindLabel'),
        width: 115,
        valueGetter: ({ data }) => (data ? codeTypeLabel(codeTypeOf(data), t) : ''),
      },
      {
        field: 'redirectUrl',
        headerName: t('qrCode.printedUrl'),
        flex: 3,
        minWidth: 200,
        cellRenderer: ({ data }: ICellRendererParams<QrCode>) =>
          data && (
            <span className="d-flex align-items-center gap-2 w-100">
              <span className="mono text-truncate cell-truncate" title={data.redirectUrl}>
                {data.redirectUrl}
              </span>
              <CopyButton value={data.redirectUrl} />
            </span>
          ),
      },
      {
        field: 'targetUrl',
        headerName: t('qrCode.destination'),
        flex: 2,
        minWidth: 160,
        valueGetter: ({ data }) =>
          data?.kind === 'File' ? (data.files.find((file) => file.isLive)?.fileName ?? '') : data?.targetUrl,
        cellRenderer: ({ data }: ICellRendererParams<QrCode>) =>
          data?.kind === 'File' ? (
            <FileCell qrCode={data} />
          ) : data && (
            <a
              href={data.targetUrl}
              target="_blank"
              rel="noreferrer noopener"
              title={data.targetUrl}
              className="d-flex align-items-center gap-1 w-100"
            >
              <span className="mono text-truncate cell-truncate">{data.targetUrl}</span>
              {data.languageTargets.length > 0 && (
                <span
                  className="badge badge-neutral flex-shrink-0"
                  title={data.languageTargets.map((target) => target.language).join(', ')}
                >
                  {t('qrCode.languages.count', { count: data.languageTargets.length })}
                </span>
              )}
              <i className="pi pi-external-link small flex-shrink-0" aria-hidden="true" />
            </a>
          ),
      },
      {
        field: 'productName',
        headerName: t('qrCode.product'),
        flex: 1,
        minWidth: 120,
        valueGetter: ({ data }) =>
          data?.kind === 'Product' ? (data.productName ?? data.productRouCode ?? '') : '',
        cellRenderer: ({ data }: ICellRendererParams<QrCode>) =>
          data?.kind === 'Product' ? (
            <span className="d-flex flex-column lh-sm">
              <span className="text-truncate cell-truncate">
                {data.productName ?? data.productRouCode}
              </span>
              <span className="mono small text-body-secondary">
                {data.productRouCode} &middot; {data.productCountryCode}
              </span>
            </span>
          ) : null,
      },
      {
        field: 'totalScans',
        headerName: t('qrCode.scans'),
        type: 'numericColumn',
        width: 90,
      },
      {
        field: 'ownerGroupName',
        headerName: t('common.group'),
        flex: 1,
        minWidth: 100,
        valueGetter: ({ data }) => (data ? ownerGroupLabel(data, t, personalSpaceId) : ''),
      },
      {
        field: 'isActive',
        headerName: t('qrCode.status'),
        width: 130,
        // Archived first: it is the reason the code is not redirecting, so it is the answer that matters.
        valueGetter: ({ data }) =>
          data?.isArchived ? 'archived' : data?.isActive ? 'active' : 'inactive',
        cellRenderer: ({ data }: ICellRendererParams<QrCode>) => {
          if (!data) {
            return null;
          }

          const state = data.isArchived ? 'archived' : data.isActive ? 'active' : 'inactive';
          const label = { archived: t('qrCode.archived'), active: t('qrCode.active'), inactive: t('qrCode.inactive') };

          return (
            <span className="d-inline-flex align-items-center gap-2">
              {/* The same dot as the detail page, and the word beside it, so colour is never the only cue. */}
              <i className={`pi pi-circle-fill status-dot ${state}`} aria-hidden="true" />
              {label[state]}
            </span>
          );
        },
      },
      {
        colId: 'actions',
        headerName: narrow ? '' : t('common.actions'),
        sortable: false,
        filter: false,
        width: narrow ? 132 : 150,
        cellRenderer: ({ data }: ICellRendererParams<QrCode>) =>
          data && (
            <span className="d-inline-flex align-items-center gap-2">
              <Link
                to={`/qr/${data.id}`}
                className="btn btn-sm btn-outline-primary"
                title={t('common.details')}
              >
                <i className="pi pi-eye" aria-hidden="true" />
              </Link>

              {data.canEdit && data.isArchived && onRestore && (
                <button
                  type="button"
                  className="btn btn-sm btn-outline-primary"
                  title={t('qrCode.restore')}
                  aria-label={`${t('qrCode.restore')} ${data.title}`}
                  onClick={() => handlers.current.onRestore?.(data)}
                >
                  <i className="pi pi-replay" aria-hidden="true" />
                </button>
              )}

              {data.canEdit && !data.isArchived && onArchive && (
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  title={t('qrCode.archive')}
                  aria-label={`${t('qrCode.archive')} ${data.title}`}
                  onClick={() => handlers.current.onArchive?.(data)}
                >
                  <i className="pi pi-inbox" aria-hidden="true" />
                </button>
              )}

              {/* Deleting frees the link identifier for reuse, so it is offered to super admins only. */}
              {data.canDelete && onDelete && (
                <button
                  type="button"
                  className="btn btn-sm btn-outline-danger"
                  title={t('common.delete')}
                  aria-label={`${t('common.delete')} ${data.title}`}
                  onClick={() => handlers.current.onDelete?.(data)}
                >
                  <i className="pi pi-trash" aria-hidden="true" />
                </button>
              )}
            </span>
          ),
      },
    ],
    // The handlers are read through a ref, so they are deliberately not dependencies.
    [t, narrow, stateWithName, personalSpaceId, Boolean(onArchive), Boolean(onRestore), Boolean(onDelete)],
  );

  const shown = useMemo(
    () =>
      (kept ? columns.filter((column) => kept.includes(column.field ?? column.colId ?? '')) : columns).filter(
        (column) => showProduct || column.field !== 'productName',
      ),
    // kept is derived from the same queries the columns are, so it changes with them.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [columns, phone, compact, medium, large, showProduct],
  );

  return (
    <DataGrid
      rows={qrCodes}
      columns={shown}
      getRowId={rowId}
      label={t('dashboard.title')}
      // A thousand codes would otherwise be a page tens of thousands of pixels tall.
      pageSize={10}
      emptyMessage={emptyMessage}
      loading={loading}
      getRowClass={rowClass}
      postSortRows={sortArchivedLast}
      onSelectionChange={onSelectionChange}
    />
  );
}

function FileCell({ qrCode }: { qrCode: QrCode }) {
  const { t } = useTranslation();
  const live = qrCode.files.find((file) => file.isLive);
  const checking = qrCode.files[0]?.status === 'Scanning';

  return (
    <span className="d-flex align-items-center gap-1 w-100">
      <i className="pi pi-file flex-shrink-0" aria-hidden="true" />
      {live ? (
        <a href={qrCode.redirectUrl} target="_blank" rel="noreferrer noopener" title={live.fileName} className="text-truncate cell-truncate">
          {live.fileName}
        </a>
      ) : (
        <span className="text-body-secondary text-truncate cell-truncate">{t('qrCode.file.noneLive')}</span>
      )}
      {checking && (
        <span className="badge badge-neutral flex-shrink-0" title={t('qrCode.file.pending', { name: qrCode.files[0].fileName })}>
          <i className="pi pi-spin pi-spinner me-1" aria-hidden="true" />
          {t('qrCode.file.checking')}
        </span>
      )}
    </span>
  );
}
